import { HTTP_STATUS_CODES } from '@/configs/constants.ts'
import { ProfileService } from '@/services/profile.service'
import i18n from '@/plugins/i18n'
import { toast } from 'vue3-toastify'

const getIbanValidationText = (user) => {
  if (!user?.iban_status) return null
  return ProfileService.getMessageByStatusIban(user.iban_status, user.admin_do_match)
}

const mapUserBase = (user, { includeCompany = false } = {}) => {
  const mapped = {
    id: user.id,
    plainId: user.plainId,
    name: user.name,
    email: user.email,
    iban_number: user.iban_number,
    iban_status: user.iban_status,
    admin_do_match: user.admin_do_match,
    created_at: user.created_at,
    points: user.points,
    street: user.street ?? user.profile?.street,
    postal_code: user.postal_code ?? user.profile?.postal_code,
    city: user.city ?? user.profile?.city,
    kick_back_fee: user.kick_back_fee,
  }

  if (includeCompany) {
    mapped.company = user.company
  }

  return mapped
}

const mapUserWithStatus = (user, { withIbanText = false, includeCompany = false, includeValidationKey = withIbanText } = {}) => {
  const mapped = {
    ...mapUserBase(user, { includeCompany }),
    last_login: user.last_login,
    is_active: user.is_active,
    rejected: user.rejected,
    is_online: user.is_online,
    modelRoles: Array.isArray(user.modelRoles) ? user.modelRoles : []
  }

  if (includeValidationKey) {
    mapped.text_validate = withIbanText ? getIbanValidationText(user) : null
  }

  return mapped
}

const mapUserWithDealer = (
  user,
  { withIbanText = false, withCustomerData = false, includeCompany = false, includeValidationKey = withIbanText } = {}
) => {
  const mapped = {
    ...mapUserBase(user, { includeCompany }),
    dealer_id: user.dealer_id
  }

  if (includeValidationKey) {
    mapped.text_validate = withIbanText ? getIbanValidationText(user) : null
  }

  if (withCustomerData) {
    mapped.customer_number = user.customer_number
    mapped.request_from = user.request_from
  }

  return mapped
}

const splitGroupsByParent = (groups = []) => ({
  parents: groups.filter((item) => item.is_main),
  children: groups.filter((item) => !item.is_main)
})

const mapDealerOption = (user, { includeEmail = false, includeHashId = false } = {}) => {
  let label = includeEmail ? `${user.name}, ${user.email}` : user.name
  if (user.group_name) {
    label = `${label}, ${user.group_name}`
  }

  const mapped = {
    value: user.id,
    label,
    name: user.name || user.company || '',
    email: user.email || '',
    group_name: user.group_name || null,
    group_id: user.group_id ?? null,
  }

  if (includeHashId) {
    mapped.hashId = user.hashId ?? user.hashid ?? user.hash_id ?? null
  }

  return mapped
}

const isOk = (response) => response?.status === HTTP_STATUS_CODES.OK

const commitPagination = (commit, links) => {
  commit('SET_PAGINATION', links ?? null)
}

const commitPaginationFromMeta = (commit, response) => {
  commitPagination(commit, response?.data?.meta?.links)
}

const commitPaginationFromLinks = (commit, response) => {
  commitPagination(commit, response?.data?.links)
}

const getErrorMessage = (error, fallbackKey = 'message.toastMessages.error') => {
  return error?.response?.data?.message || error?.response?.data?.data?.message || error?.message || i18n.global.t(fallbackKey)
}

const showErrorToast = (error, fallbackKey, { force = false } = {}) => {
  if (!force && error?.response) {
    return
  }
  toast.clearAll()
  toast(getErrorMessage(error, fallbackKey), {
    type: 'error',
    theme: 'colored',
    transition: toast.TRANSITIONS.SLIDE,
    position: toast.POSITION.TOP_RIGHT
  })
}

export {
  getIbanValidationText,
  mapUserBase,
  mapUserWithStatus,
  mapUserWithDealer,
  splitGroupsByParent,
  mapDealerOption,
  isOk,
  commitPagination,
  commitPaginationFromMeta,
  commitPaginationFromLinks,
  getErrorMessage,
  showErrorToast
}
